import { useCallback, useEffect, useRef, useState } from "react";
import TabBar from "../../components/TabBar";
import { useAppDispatch, useAppSelector } from "../../app/hooks";
import { RootState } from "../../app/store";
import {
	listDeviceData,
	listOwnDeviceData,
	readDeviceImage,
	readLatestDeviceData,
	readOwnLatestDeviceData,
} from "./slice";
import AlertError from "../../components/AlertError";
import SevenSegmentDisplay from "../../components/SevenSegmentDisplay";
import ImageByKey from "../../components/ImageByKey";
import CustomLineChar from "../../components/CustomLineChart";
import CustomTable from "../../components/CustomTable";
import { useNavigate } from "react-router-dom";
import { getDeviceState, getErrorMessages } from "../../libs/deviceFunctions";
import DateRangePicker from "../../components/DateRangePicker";
import { getEndOfDay, getStartOfDay } from "../../libs/utils";

interface Range {
	startDate: null | Date;
	endDate: null | Date;
}

export default function Home() {
	const navigate = useNavigate();
	const appDispatch = useAppDispatch();
	const { device, deviceData, loading, report } = useAppSelector(
		(state: RootState) => state.homeReducer,
	);
	const { user, groups }: any = useAppSelector(
		(state: RootState) => state.commonReducer,
	);
	const { currentUser } = useAppSelector(
		(state: RootState) => state.profileReducer,
	);
	const [errorTitle, setErrorTitle] = useState("");
	const [errors, setErrors] = useState<string[]>([]);
	const isAdmin = !!groups.find((group: any) => group.name === "Administradores");
	const isOwnUser = !currentUser || user.username === currentUser.username;

	const handleReadLatestDeviceData = useCallback(async () => {
		try {
			if (!device || !device.id) {
				setErrorTitle("Erro ao ler dados");
				setErrors(["Nenhum dispositivo selecionado"]);
				return;
			}
			const response = !isAdmin || isOwnUser
				? await appDispatch(readOwnLatestDeviceData({ deviceId: device.id })).unwrap()
				: await appDispatch(readLatestDeviceData({ username: currentUser.username, deviceId: device.id })).unwrap();
			return response;
		} catch (error: any) {
			console.log(error);
			setErrorTitle("Erro ao ler dados");
			if (error.code === "#DeviceDataNotFound") {
				setErrors(["Não há dados desse dispositivo"]);
			} else {
				setErrors(["Erro inesperado"]);
			}
		}
	}, [isAdmin, isOwnUser, device, currentUser, appDispatch, navigate]);

	const [tabs, setTabs] = useState([
		{ name: "Status", current: true, onClick: (tabIndex: number) => tabIndex },
		{
			name: "Gráficos",
			current: false,
			onClick: (tabIndex: number) => tabIndex,
		},
		{
			name: "Alarmes",
			current: false,
			onClick: (tabIndex: number) => tabIndex,
		},
	]);

	useEffect(() => {
		handleReadLatestDeviceData();
	}, [handleReadLatestDeviceData]);

	useEffect(() => {
		if (tabs[0].current) {
			const interval = setInterval(() => {
				handleReadLatestDeviceData();
			}, 120000);
			return () => clearInterval(interval);
		}
	}, [tabs, handleReadLatestDeviceData]);

	const handleChangeTab = useCallback(
		(tabIndex: number) => {
			let _tabs = [...tabs];
			_tabs.forEach((tab, index) => {
				tab.current = tabIndex === index;
			});
			setTabs(_tabs);
		},
		[tabs],
	);

	const createdAt = new Date(deviceData && deviceData.createdAt);
	const isEnabled = new Date().getTime() - createdAt.getTime() < 3 * 60 * 1000;

	const lastUpdated = createdAt
		.toLocaleString("pt-BR", {
			timeZone: "America/Sao_Paulo",
			day: "2-digit",
			month: "2-digit",
			year: "numeric",
			hour: "2-digit",
			minute: "2-digit",
			second: "2-digit",
			hour12: false,
		})
		.replace(",", "");

	const THREE_HOURS_IN_MS = -3 * 60 * 60 * 1000;

	const [dateRange, setDateRange] = useState<Range>({
		startDate: getStartOfDay(new Date()),
		endDate: getEndOfDay(new Date()),
	});

	const handleDateRangeChange = useCallback(async () => {
		if (dateRange.startDate && dateRange.endDate) {
			const diffInMs =
				dateRange.endDate.getTime() - dateRange.startDate.getTime();
			const maxRangeMs = 4 * 24 * 60 * 60 * 1000;
			if (diffInMs > maxRangeMs) {
				setErrorTitle("Intervalo de datas inválido");
				setErrors(["Por favor, selecione um intervalo de no máximo 4 dias"]);
				return;
			} else {
				setErrorTitle("");
				setErrors([]);
			}
		}

		let startDate, endDate;
		if (
			dateRange.startDate &&
			dateRange.endDate &&
			new Date(dateRange.startDate).toDateString() ===
			new Date(dateRange.endDate).toDateString()
		) {
			const startOfDay = getStartOfDay(new Date(dateRange.startDate));
			const nextDay = new Date(dateRange.startDate);
			nextDay.setDate(nextDay.getDate() + 1);
			startDate = new Date(startOfDay.getTime() - THREE_HOURS_IN_MS);
			endDate = new Date(getStartOfDay(nextDay).getTime() - THREE_HOURS_IN_MS);
		} else {
			startDate = new Date(
				getStartOfDay(dateRange.startDate || new Date()).getTime() -
				THREE_HOURS_IN_MS,
			);
			endDate = new Date(
				getEndOfDay(dateRange.endDate || new Date()).getTime() - THREE_HOURS_IN_MS,
			);
		}

		const response = !isAdmin || isOwnUser
			? await appDispatch(
				listOwnDeviceData({
					deviceId: device.id,
					startDate,
					endDate,
				})
			).unwrap()
			: await appDispatch(
				listDeviceData({
					username: currentUser.username,
					deviceId: device.id,
					startDate,
					endDate,
				})
			).unwrap();

		return response;
	}, [dateRange, isOwnUser, device, currentUser, appDispatch]);

	useEffect(() => {
		handleDateRangeChange();
	}, [dateRange, tabs, handleDateRangeChange]);

	const errorsTableRef = useRef(null);

	const getAdjustedTimestamp = (timestamp?: string | number | Date) => {
		if (!timestamp) {
			return (dateRange.startDate?.getTime() || 0) + THREE_HOURS_IN_MS;
		}
		return new Date(timestamp).getTime() + THREE_HOURS_IN_MS;
	};
	const reportColumns: any = useRef([
		{ title: "Alarme", type: "string", key: "alarm", hidden: false },
		{ title: "Id", type: "string", key: "parameter", hidden: false },
		{ title: "Bit", type: "string", key: "bit", hidden: false },
		{ title: "Descrição", type: "string", key: "description", hidden: false },
		{
			title: "Tipo",
			type: "string",
			key: "type",
			hidden: false,
			render: (rowData: any) => (rowData.type === "error" ? "Erro" : "Aviso"),
		},
		{ title: "Criado em", type: "date-time", key: "createdAt", hidden: false },
	]);

	return (
		<div className="flex w-full h-full justify-start items-center flex-col">
			<div className="w-full lg:px-10 lg:pt-2 sm:p-2">
				<TabBar tabs={tabs} onChangeTab={handleChangeTab} />
			</div>
			{/* Status view */}
			<span
				hidden={!tabs[0].current}
				className="w-full lg:px-10 pt-2 sm:p-2 mt-2"
			>
				<div className="flex flex-col gap-8">
					{/* Seção de Cabeçalho */}
					<div className="flex flex-col lg:flex-row items-center gap-4 p-4 border border-gray-300 rounded-lg shadow">
						{/* Info do cabeçalho */}
						<div>
							<div className="flex flex-col gap-2 flex-1">
								<dl>
									<dt className="text-2xl font-semibold text-gray-900">
										{device.name}
									</dt>
									<dd className="text-lg text-gray-700">
										{deviceData?.data?.["Devicetype.BM_s_DriveName"]}
									</dd>
								</dl>
								<dl>
									<dt className="text-lg font-semibold text-gray-900">
										{isEnabled ? "Dados atualizados" : "Sem conexão"}
									</dt>
									{deviceData && (
										<dd className="text-lg text-gray-700">
											Última atualização: {lastUpdated}
										</dd>
									)}
								</dl>
							</div>
							<div className="flex items-center justify-center">
								<SevenSegmentDisplay
									number={getDeviceState(
										parseInt(
											deviceData?.data?.["Drivemanager.BM_w_Statusword"] || "0",
											10,
										),
									)}
								/>
							</div>
						</div>
						{/* Imagem do cabeçalho */}
						<div className="flex items-center justify-center lg:ml-auto">
							<ImageByKey
								className="object-contain max-h-[200px]"
								imageKey={device.deviceImageKey}
								readFileRequest={async (imageKey) =>
									appDispatch(readDeviceImage(imageKey)).unwrap()
								}
							/>
						</div>
					</div>

					{/* Seção de Informações do Drive */}
					<div className="p-4 border border-gray-300 rounded-lg shadow">
						<h3 className="text-xl font-semibold text-gray-900 mb-4">
							Informações do drive
						</h3>
						<div className="grid lg:grid-cols-1 md:grid-cols-2 gap-4">
							<div className="flex justify-between items-center">
								<dt className="text-sm font-medium text-gray-900">
									Temperatura interna
								</dt>
								<dd className="text-sm text-gray-700">
									{
										deviceData?.data?.[
										"ConfigurationPowerUnit.BM_u_AmpActAmbientTemp"
										]
									}
									°C
								</dd>
							</div>
							<div className="flex justify-between items-center">
								<dt className="text-sm font-medium text-gray-900">
									Temperatura do dissipador
								</dt>
								<dd className="text-sm text-gray-700">
									{
										deviceData?.data?.[
										"ConfigurationPowerUnit.BM_u_AmpActHeatsinkTemp"
										]
									}
									°C
								</dd>
							</div>
							<div className="flex justify-between items-center">
								<dt className="text-sm font-medium text-gray-900">
									Valor atual de IxT
								</dt>
								<dd className="text-sm text-gray-700">
									{(
										(100 *
											deviceData?.data?.[
											"ConfigurationPowerUnit.BM_u_AmpAct_Ixt"
											]) /
										8192
									).toFixed(1)}
									%
								</dd>
							</div>
							<div className="flex justify-between items-center">
								<dt className="text-sm font-medium text-gray-900">
									Tensão link DC
								</dt>
								<dd className="text-sm text-gray-700">
									{(
										(540 *
											deviceData?.data?.[
											"ConfigurationPowerUnit.BM_u_AmpActDCLinkVolt"
											]) /
										16384
									).toFixed(2)}
									V
								</dd>
							</div>
						</div>
					</div>

					{/* Seção de Informações do Motor */}
					<div className="p-4 border border-gray-300 rounded-lg shadow">
						<h3 className="text-xl font-semibold text-gray-900 mb-4">
							Informações do motor
						</h3>
						<div className="grid lg:grid-cols-1 md:grid-cols-2 gap-4">
							<div className="flex justify-between items-center">
								<dt className="text-sm font-medium text-gray-900">
									Temperatura
								</dt>
								<dd className="text-sm text-gray-700">
									{deviceData?.data?.["ConfigurationMotor.BM_i_MotorActTemp"]}°C
								</dd>
							</div>
							<div className="flex justify-between items-center">
								<dt className="text-sm font-medium text-gray-900">
									Valor de I²t
								</dt>
								<dd className="text-sm text-gray-700">
									{(100 * deviceData?.data?.["ConfigurationMotor.BM_ud_MotorAct_I2t"] / 16384).toFixed(1)}%
								</dd>
							</div>
						</div>
					</div>
				</div>
			</span>
			{/* Charts view */}
			<span
				hidden={!tabs[1].current}
				className="w-full lg:px-10 lg:pt-2 sm:p-2 mt-2"
			>
				<DateRangePicker
					onChange={(newDateRange: Range) => setDateRange(newDateRange)}
					defaultDate={dateRange}
				/>
				<div className="grid grid-cols-1 lg:grid-cols-3 gap-5 mt-5">
					<div className="w-full max-w-[420px] flex flex-col justify-center">
						<CustomLineChar
							data={report.map((devData: any) => ({
								y:
									(devData &&
										devData.data &&
										devData.data["ConfigurationMotor.BM_i_MotorActTemp"]) ||
									0,
								x: getAdjustedTimestamp(
									devData && devData.createdAt
										? devData.createdAt
										: undefined,
								),
							}))}
							seriesName={"Temperatura atual do motor"}
							minX={
								(dateRange.startDate || new Date()).getTime() +
								THREE_HOURS_IN_MS
							}
							loading={loading}
						/>
					</div>
					<div className="w-full max-w-[420px] flex flex-col justify-center">
						<CustomLineChar
							data={report.map((devData: any) => ({
								y:
									(devData &&
										devData.data &&
										(100 *
											devData.data["ConfigurationPowerUnit.BM_u_AmpAct_Ixt"]) /
										8192) ||
									0,
								x: getAdjustedTimestamp(
									devData && devData.createdAt
										? devData.createdAt
										: undefined,
								),
							}))}
							seriesName={"Valor atual de IxT"}
							minX={
								(dateRange.startDate || new Date()).getTime() +
								THREE_HOURS_IN_MS
							}
							decimals={1}
							suffix="%"
							loading={loading}
						/>
					</div>
					<div className="w-full max-w-[420px] flex flex-col justify-center">
						<CustomLineChar
							data={report.map((devData: any) => ({
								y:
									(devData &&
										devData.data &&
										(100 *
											devData.data["ConfigurationMotor.BM_ud_MotorAct_I2t"]) /
										16384) ||
									0,
								x: getAdjustedTimestamp(
									devData && devData.createdAt
										? devData.createdAt
										: undefined,
								),
							}))}
							seriesName={"Valor atual de I²t"}
							minX={
								(dateRange.startDate || new Date()).getTime() +
								THREE_HOURS_IN_MS
							}
							decimals={1}
							suffix="%"
							loading={loading}
						/>
					</div>
				</div>
			</span>
			{/* Alarms view */}
			<span
				hidden={!tabs[2].current}
				className="w-full lg:px-10 lg:pt-2 sm:p-2 mt-2"
			>
				<DateRangePicker
					onChange={(newDateRange: Range) => setDateRange(newDateRange)}
					defaultDate={dateRange}
				/>
				<div className="w-full mt-5">
					<CustomTable
						ref={errorsTableRef}
						data={report
							.map((rowData: any) => {
								return getErrorMessages(rowData.data).map((errorMessage) => ({
									...errorMessage,
									createdAt: rowData.createdAt,
								}));
							})
							.flat()}
						loading={loading}
						isSearchable={false}
						title={"Erros dispositivo"}
						subtitle={"Todos os erros do dispositivo"}
						columns={reportColumns.current}
						editLabel={""}
						isRelational={true}
						refresh={false}
						readFileRequest={async (imageKey) =>
							appDispatch(readDeviceImage(imageKey)).unwrap()
						}
						defaultSortKey="createdAt"
						defaultSortOrder="desc"
						labelRows="linhas"
					/>
				</div>
			</span>
			<AlertError
				onClose={() => setErrors([])}
				errorTitle={errorTitle}
				errors={errors}
				duration={5000}
				position="bottom-right"
			/>
		</div>
	);
}
